/*
 * Day-detail modal — opens when the teacher taps any cell in the
 * Calendar grid. Designed to be a teaching tool for younger grades:
 * "Today is …", with optional "Yesterday was …" / "Tomorrow is …"
 * helpers (gated by the K-2 preference) in the chalkboard-blue
 * teaching color. Holidays, events, birthdays — anything tied to that
 * day — surface here too.
 *
 * Visual: larger than v1, frosted-glass card with a generous lift
 * shadow + accent ring so it reads as a clearly-separate teaching
 * panel sitting in front of the month grid. Backdrop is a softly
 * blurred dim so the popup is the unmistakable focus.
 */

.backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 18, 12, 0.5);
  backdrop-filter: blur(10px) saturate(140%);
  -webkit-backdrop-filter: blur(10px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: clamp(20px, 3vw, 48px);
  /* Soft fade-in so the popup doesn't pop in jaggedly when the
   * teacher taps a cell mid-meeting. */
  animation: dayDetailBackdrop 180ms ease-out;
}

.panel {
  position: relative;
  width: 100%;
  max-width: min(1080px, 95vw);
  padding: clamp(28px, 3vw, 48px) clamp(32px, 3.5vw, 56px);
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 1.6vw, 26px);
  font-family: var(--token-font-body);
  color: var(--token-text-primary);
  /* Lifted accent ring + deep multi-layer shadow for the "3D pop" feel.
   * Consumes the shared `glass glass--solid` utilities from app.css
   * for the frosted backdrop blur + cream tint, but overrides the
   * cream-tinted base with a brighter, whiter fill so packed days
   * stay readable while still reading as a window onto the calendar
   * behind it. */
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(28px) saturate(170%);
  -webkit-backdrop-filter: blur(28px) saturate(170%);
  outline: 1.5px solid color-mix(in srgb, var(--token-accent) 55%, transparent);
  outline-offset: -1.5px;
  border-radius: 28px;
  box-shadow:
    0 60px 120px -20px rgba(8, 18, 12, 0.55),
    0 24px 48px -12px rgba(8, 18, 12, 0.4),
    inset 0 2px 0 rgba(255, 255, 255, 0.85);
  animation: dayDetailPanel 220ms cubic-bezier(0.22, 0.9, 0.3, 1.15);
}

@keyframes dayDetailBackdrop {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes dayDetailPanel {
  from {
    transform: translateY(8px) scale(0.96);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

/* ---------- Header — Today, then Yesterday/Tomorrow boxes ---------- */

.header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: clamp(12px, 1.2vw, 20px);
  /* Subtle dotted underline separates the teaching headline from the
   * events/birthdays below it, mirroring the orange dotted divider on
   * the parent Calendar card. */
  padding-bottom: clamp(14px, 1.4vw, 22px);
  border-bottom: 2px dotted color-mix(in srgb, var(--token-accent) 45%, transparent);
}

.todayLine {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(32px, 3.4vw, 52px);
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1.15;
  color: var(--token-chalkboard-blue);
}

.todayLead {
  font-family: var(--token-font-display);
  color: var(--token-chalkboard-blue);
  /* Slightly lighter weight + opacity so "Today is" reads as a label
   * leading into the day-of-week, not as its own heading. */
  font-weight: 600;
  opacity: 0.78;
  margin-right: 8px;
}

/* The full "May 14, 2026" date — sits on its own line directly under
 * "Today is Friday" at the same big headline size so kids in the back
 * row can read it clearly. Slightly lighter weight + softer opacity
 * keeps the weekday as the primary teaching focus. */
.todayDate {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(32px, 3.4vw, 52px);
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: 0.3px;
  color: var(--token-chalkboard-blue);
  opacity: 0.78;
}

/* Yesterday / Tomorrow sit side-by-side under "Today is …". On tighter
 * widths they wrap to stacked. Each lives in its own colored teaching
 * box — chalkboard blue for yesterday, chalkboard green for tomorrow —
 * so the trio reads as a left/center/right time axis. */
.relativeRow {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(10px, 1.2vw, 18px);
  width: 100%;
}

.relativeBox {
  flex: 1 1 280px;
  /* Single-line layout — "Yesterday was Thursday" reads as one line
   * with the lead label + weekday in a row, not stacked. */
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: clamp(6px, 0.7vw, 12px);
  padding: clamp(12px, 1.2vw, 18px) clamp(18px, 2vw, 32px);
  border-radius: 18px;
  font-family: var(--token-font-body);
  text-align: center;
  min-width: 0;
  white-space: nowrap;
  /* Glassy 3D button look — backdrop blur + layered shadows for depth
   * (outer soft drop + tight contact) and inset highlights (top sheen,
   * bottom shadow, edge ring) for the curved-glass surface feel.
   * Mirrors the thermometer-tube + readout treatment elsewhere. */
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  box-shadow:
    0 10px 24px -8px rgba(8, 18, 12, 0.28),
    0 2px 6px rgba(8, 18, 12, 0.12),
    inset 0 0 0 1.5px rgba(255, 255, 255, 0.55),
    inset 0 3px 8px rgba(255, 255, 255, 0.55),
    inset 0 -3px 6px rgba(8, 18, 12, 0.12);
}

.relativeYesterday {
  background: rgba(15, 134, 155, 0.22);
  color: var(--token-chalkboard-blue);
}

.relativeTomorrow {
  background: rgba(21, 189, 108, 0.22);
  color: var(--token-chalkboard-green);
}

/* Same size as the .todayLine headline so the trio reads as one
 * teaching unit. Lead has slightly lighter weight + softer opacity
 * (mirrors .todayLead) so "Yesterday was" reads as a label leading
 * into the date. */
.relativeLead {
  font-family: var(--token-font-body);
  /* Two type-scale steps smaller (÷1.2²) than the .todayLine headline
   * so the single-line "Yesterday was X" fits without wrap. */
  font-size: clamp(23px, 2.3vw, 36px);
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: 0.3px;
  opacity: 0.78;
}

.relativeDate {
  font-family: var(--token-font-body);
  font-size: clamp(23px, 2.3vw, 36px);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0.3px;
}

/* ---------- Close button ---------- */

.close {
  position: absolute;
  top: 16px;
  right: 16px;
  min-width: 44px;
  min-height: 44px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(28, 38, 32, 0.18);
  background: rgba(255, 255, 255, 0.7);
  color: rgba(28, 38, 32, 0.7);
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background 120ms ease,
    color 120ms ease;
}

.close:hover {
  background: #fff;
  color: var(--token-text-primary);
}

/* ---------- Holiday / fun pills ---------- */

.holiday,
.fun {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 999px;
  font-family: var(--token-font-body);
  font-size: clamp(15px, 1.3vw, 19px);
  font-weight: 700;
  letter-spacing: 0.3px;
  align-self: center;
  text-align: center;
}

.holiday {
  background: color-mix(in srgb, var(--token-accent) 22%, transparent);
  border: 1.5px solid color-mix(in srgb, var(--token-accent) 60%, transparent);
  color: rgba(140, 78, 30, 0.95);
}

.fun {
  background: rgba(15, 134, 155, 0.12);
  border: 1.5px solid rgba(15, 134, 155, 0.4);
  color: var(--token-chalkboard-blue);
}

.pillIcon {
  font-size: 1.05em;
}

/* ---------- Sections (Birthdays, Events) ---------- */

.section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sectionTitle {
  margin: 0;
  font-family: var(--token-font-body);
  font-size: clamp(20px, 1.8vw, 26px);
  font-weight: 700;
  color: var(--token-text-primary);
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.listItem {
  padding: 12px 18px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(28, 38, 32, 0.12);
  font-family: var(--token-font-body);
  font-size: clamp(16px, 1.4vw, 22px);
  color: var(--token-text-primary);
  line-height: 1.4;
}

/* ---------- Birthday section + confetti ---------- */

/* Lives at the very bottom of the popup. Each kid gets a centered
 * "🎈 Happy birthday Name! 🎈" line. The CSS confetti pieces fall
 * behind the names so the section feels celebratory without needing
 * any JS. */
.birthdaySection {
  position: relative;
  margin-top: clamp(4px, 0.6vw, 10px);
  padding: clamp(20px, 2vw, 32px) clamp(16px, 1.8vw, 28px);
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(255, 226, 188, 0.65),
    rgba(255, 244, 224, 0.55) 60%,
    rgba(255, 222, 230, 0.65)
  );
  /* Same glassy 3D treatment as .relativeBox so the birthday section
   * reads as a peer "card" rather than just a colored panel. */
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  box-shadow:
    0 10px 24px -8px rgba(8, 18, 12, 0.28),
    0 2px 6px rgba(8, 18, 12, 0.12),
    inset 0 0 0 1.5px rgba(255, 255, 255, 0.55),
    inset 0 3px 8px rgba(255, 255, 255, 0.55),
    inset 0 -3px 6px rgba(8, 18, 12, 0.12);
  overflow: hidden;
  isolation: isolate;
}

.confetti {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.confettiPiece {
  position: absolute;
  top: -14px;
  border-radius: 2px;
  opacity: 0.85;
  animation-name: confettiFall;
  animation-timing-function: ease-in;
  animation-iteration-count: infinite;
  will-change: transform, opacity;
}

@keyframes confettiFall {
  0% {
    transform: translateY(-20px) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 0.9;
  }
  90% {
    opacity: 0.9;
  }
  100% {
    transform: translateY(180px) rotate(540deg);
    opacity: 0;
  }
}

.birthdayList {
  position: relative;
  z-index: 1;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.birthdayItem {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-family: var(--token-font-body);
  font-size: clamp(22px, 2.2vw, 36px);
  font-weight: 700;
  letter-spacing: 0.3px;
  color: var(--token-accent);
  line-height: 1.25;
  text-align: center;
  /* Tiny lift so "Happy birthday!" reads cleanly over the soft
   * gradient + confetti behind it. */
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.7);
}

.balloons {
  font-size: 1.1em;
  line-height: 1;
}

@media (prefers-reduced-motion: reduce) {
  .confettiPiece {
    animation: none;
    opacity: 0.55;
  }
}
